import { useCallback, useState } from 'react';

import { getStoredKioskIds, setStoredKioskIds } from '../utils/schedule-storage';

type UseScheduleKiosksReturn = {
  selectedKioskIds: string[];
  updateSelectedKioskIds: (ids: string[]) => void;
  removeKiosk: (kioskId: string) => void;
};

/**
 * Hook for managing selected kiosk IDs with localStorage persistence.
 * Initializes state from localStorage on mount and syncs all changes back to storage.
 *
 * @returns {UseScheduleKiosksReturn} Kiosk selection state and update functions:
 *   - `selectedKioskIds` - Currently selected kiosk IDs
 *   - `updateSelectedKioskIds` - Replaces all selected kiosks and persists to storage
 *   - `removeKiosk` - Removes a single kiosk by ID and persists to storage
 */
export const useScheduleKiosks = (): UseScheduleKiosksReturn => {
  const [selectedKioskIds, setSelectedKioskIds] = useState<string[]>(getStoredKioskIds);

  const updateSelectedKioskIds = useCallback((ids: string[]) => {
    setSelectedKioskIds(ids);
    setStoredKioskIds(ids);
  }, []);

  const removeKiosk = useCallback(
    (kioskId: string) => {
      const newIds = selectedKioskIds.filter((id) => id !== kioskId);
      updateSelectedKioskIds(newIds);
    },
    [selectedKioskIds, updateSelectedKioskIds],
  );

  return {
    selectedKioskIds,
    updateSelectedKioskIds,
    removeKiosk,
  };
};
